class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    PredictiveSearch.instanceCount = (PredictiveSearch.instanceCount || 0) + 1;
    const inputId = this.input.id || `PredictiveSearchInput-${PredictiveSearch.instanceCount}`;
    if (!this.input.id) this.input.id = inputId;
    const safeInputId = inputId.replace(/[^a-zA-Z0-9_-]/g, '-');
    this.resultsIdPrefix = safeInputId;
    this.resultsListboxId = `${this.resultsIdPrefix}-predictive-search-results`;
    this.input.setAttribute('aria-controls', this.resultsListboxId);
    this.input.setAttribute('aria-owns', this.resultsListboxId);
    this.isOpen = false;
    this.abortController = new AbortController();
    this.requestRevision = 0;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelPendingRequest();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up')
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.focus();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length - 1 > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (selectedElement && selectedElementIndex === -1) return;

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const requestRevision = ++this.requestRevision;
    this.removeAttribute('results');
    this.setLiveRegionLoadingState();

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search request failed (${response.status})`);
        }

        return response.text();
      })
      .then((text) => {
        if (signal.aborted || requestRevision !== this.requestRevision || this.searchTerm !== searchTerm) return;

        const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
        const resultsSection = parsedHTML.querySelector('#shopify-section-predictive-search');

        if (!resultsSection?.querySelector('[role="listbox"]')) {
          throw new Error('Predictive search response is missing its results listbox');
        }

        const resultsMarkup = resultsSection.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            if (predictiveSearchInstance.cachedResults) {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          }
        );

        if (signal.aborted || requestRevision !== this.requestRevision || this.searchTerm !== searchTerm) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (signal.aborted || error?.name === 'AbortError' || error?.code === 20) return;
        if (requestRevision !== this.requestRevision) return;

        // Keep the native search form usable as the progressive fallback.
        this.removeAttribute('results');
        const loadingState = this.predictiveSearchResults?.querySelector(
          '.predictive-search__loading-state'
        );
        if (this.predictiveSearchResults) {
          this.predictiveSearchResults.replaceChildren(...(loadingState ? [loadingState] : []));
        }
        this.closeResults();
        this.isOpen = false;
      })
      .finally(() => {
        if (requestRevision === this.requestRevision) this.removeAttribute('loading');
      });
  }

  cancelPendingRequest() {
    this.requestRevision += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.removeAttribute('loading');
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    clearTimeout(this.liveRegionTimer);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    this.liveRegionTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      this.closeResults();
      return;
    }

    this.predictiveSearchResults.innerHTML = this.localizeResultsMarkup(resultsMarkup);
    this.input.setAttribute('aria-controls', this.resultsListboxId);
    this.input.setAttribute('aria-owns', this.resultsListboxId);
    this.input.setAttribute('aria-activedescendant', '');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  localizeResultsMarkup(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    const idMap = new Map();

    template.content.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const localizedId = `${this.resultsIdPrefix}-${originalId}`;
      idMap.set(originalId, localizedId);
      element.id = localizedId;
    });

    ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
      template.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const localizedReferences = element
          .getAttribute(attribute)
          .split(/\s+/)
          .filter(Boolean)
          .map((id) => idMap.get(id) || id)
          .join(' ');

        element.setAttribute(attribute, localizedReferences);
      });
    });

    const listbox = template.content.querySelector('[role="listbox"]');
    if (listbox) listbox.id = this.resultsListboxId;

    return template.innerHTML;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const resultCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(resultCount?.textContent || '');
  }

  getResultsMaxHeight() {
    const resultsTop = this.predictiveSearchResults?.getBoundingClientRect().top || 0;
    const availableViewport = Math.max(0, window.innerHeight - resultsTop - 16);
    const designCap = Math.min(window.innerHeight * 0.68, 620);
    this.resultsMaxHeight = Math.floor(Math.min(availableViewport, designCap));
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    /* Le panneau est en plein écran et ne commence plus juste sous le header.
       Il doit être mesuré une fois rendu, depuis sa position réelle, sinon le
       verrouillage du body peut rendre la fin des résultats inaccessible. */
    this.setAttribute('open', true);
    const maxHeight = this.getResultsMaxHeight();
    this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingRequest();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
